import { getPublicIndexablePathsByRouteKeys } from "@logscord/public-site/core";
import { PublicSite } from "./publicSite.js";

type JsonSchema = {
    type                  : string;
    properties?            : Record<string, JsonSchema>;
    enum?                  : string[];
    additionalProperties?  : boolean;
};

type WebMcpTool = {
    name        : string;
    description : string;
    inputSchema : JsonSchema;
    execute     : (input?: unknown) => unknown | Promise<unknown>;
};

type ModelContextProvider = {
    provideContext?: (context: { tools: WebMcpTool[] }) => void | Promise<void>;
};

declare global {
    interface Navigator {
        modelContext?: ModelContextProvider;
    }
}

const PublicWebMcpRouteKeys = [
    "docs",
    "features",
    "compare",
] as const;
const PublicDocPaths = new Set(
    getPublicIndexablePathsByRouteKeys(PublicSite, PublicWebMcpRouteKeys),
);

function getDefaultPublicDocPath() {
    const path = PublicDocPaths.values().next().value;

    if (!path)
        throw new Error("Public WebMCP requires at least one indexable public page.");

    return path;
}

const DefaultPublicDocPath = getDefaultPublicDocPath();

function getStringProperty(input: unknown, key: string) {
    if (!input || typeof input !== "object" || !(key in input))
        return null;

    const value = (input as Record<string, unknown>)[key];

    return typeof value === "string" ? value : null;
}

function navigate(path: string) {
    const url = new URL(path, window.location.origin);

    window.location.href = url.toString();

    return {
        ok  : true,
        url : url.toString(),
    };
}

export function registerPublicWebMcp() {
    if (typeof navigator === "undefined" || !navigator.modelContext?.provideContext)
        return;

    void navigator.modelContext.provideContext({
        tools: [
            {
                name        : "open_logscord_public_page",
                description : "Open a LogsCord public documentation, features or comparison page.",
                inputSchema : {
                    type       : "object",
                    properties : {
                        path: {
                            type : "string",
                            enum : Array.from(PublicDocPaths),
                        },
                    },
                    additionalProperties: false,
                },
                execute(input) {
                    const requestedPath = getStringProperty(input, "path");
                    const path = requestedPath && PublicDocPaths.has(requestedPath)
                        ? requestedPath
                        : DefaultPublicDocPath;

                    return navigate(path);
                },
            },
            {
                name        : "start_logscord_oauth",
                description : "Start the LogsCord Discord OAuth login flow in the current browser context.",
                inputSchema : {
                    type                 : "object",
                    properties           : {},
                    additionalProperties : false,
                },
                execute() {
                    return navigate("/api/oauth2");
                },
            },
        ],
    });
}
